import React, {
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
  useEffect,
} from "react";
import "./TextEditor.scss";
export interface Props {
  onClick?: any;
  ref?: any;
}
const TextEditor = forwardRef<HTMLDivElement, Props>(({ onClick }, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [textColor, setTextColor] = useState<string>("#000000");
  const [backgroundColor, setBackgroundColor] = useState<string>("#ffffff");
  const [isVisual, setIsVisual] = useState(true);
  const [htmlContent, setHtmlContent] = useState<string>("");
  const applyFormat = (command: string, value: string = "") => {
    document.execCommand(command, false, value);
  };
  // const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
  //   if (event.ctrlKey || event.metaKey) {
  //     switch (event.key) {
  //       case "b":
  //         event.preventDefault();
  //         applyFormat("bold");
  //         break;
  //       case "i":
  //         event.preventDefault();
  //         applyFormat("italic");
  //         break;
  //       case "u":
  //         event.preventDefault();
  //         applyFormat("underline");
  //         break;

  //       default:
  //         break;
  //     }
  //   }
  // };
  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const insertImageFromFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editorRef.current) return;

    const editor = editorRef.current;
    editor.focus(); // ⬅ ensure editor is focused

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;

      const imgElement = document.createElement("img");
      imgElement.src = dataUrl;
      imgElement.style.maxWidth = "100%";
      imgElement.style.height = "auto";

      const selection = window.getSelection();
      if (!selection) return;

      const range = selection.getRangeAt(0);
      range.insertNode(imgElement);
      range.collapse(false);

      // move cursor after the image
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.setStartAfter(imgElement);
      selection.addRange(newRange);
    };
    reader.readAsDataURL(file);
  };
  const insertLink = () => {
    if (!editorRef.current) return;

    editorRef.current.focus();

    const url = prompt("Enter the URL (e.g., https://example.com)")?.trim();
    if (!url) {
      alert("URL is required.");
      return;
    }

    const isValidUrl = /^(https?:\/\/[^\s/$.?#].[^\s]*)$/i.test(url);
    if (!isValidUrl) {
      alert("Please enter a valid URL (must start with http:// or https://).");
      return;
    }

    const text = prompt("Enter the link text")?.trim();
    if (!text) {
      alert("Link text is required.");
      return;
    }

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.textContent = text;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";

    const selection = window.getSelection();
    if (!selection) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(anchor);
    range.collapse(false);

    // Move cursor after the link
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.setStartAfter(anchor);
    selection.addRange(newRange);
  };

  const removeHeadings = () => {
    const editor = editorRef.current;
    if (editor) {
      const headings = editor.querySelectorAll("h1, h2, h3, h4, h5, h6");
      headings.forEach((heading) => {
        const parent = heading.parentElement;
        if (parent) {
          const textContent = heading.textContent;
          const newNode = document.createTextNode(textContent || "");
          parent.replaceChild(newNode, heading);
        }
      });
    }
  };

  const applyHeading = (headingType: string) => {
    removeHeadings();
    applyFormat("formatBlock", headingType);
  };

  const applyTextColor = (color: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return; // No text selected

    const span = document.createElement("span");
    span.style.color = color;
    span.appendChild(range.extractContents());
    range.insertNode(span);

    // Move cursor after span
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.setStartAfter(span);
    selection.addRange(newRange);
  };

  const applyBackgroundColor = (color: string) => {
    applyFormat("backColor", color);
  };

  const handleTextColorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const color = event.target.value;
    setTextColor(color);
    applyTextColor(color);
  };

  const handleBackgroundColorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const color = event.target.value;
    setBackgroundColor(color);
    applyBackgroundColor(color);
  };
  const toggleView = () => {
    const editor = editorRef.current;
    if (isVisual && editor) {
      // Save current visual content into state for HTML view
      setHtmlContent(editor.innerHTML);
    }
    setIsVisual(!isVisual);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHtmlContent(e.target.value);
  };
  useEffect(() => {
    if (isVisual && editorRef.current) {
      editorRef.current.innerHTML = htmlContent;
    }
  }, [isVisual]);
  return (
    <div className={`mfg-textEditor`}>
      <div className="toolBar">
        <button>
          Text Color
          <input
            type="color"
            value={textColor}
            onChange={handleTextColorChange}
          />
        </button>
        <button>
          Background Color
          <input
            type="color"
            value={backgroundColor}
            onChange={handleBackgroundColorChange}
          />
        </button>
        <button
          onClick={() => applyHeading("H1")}
          aria-label="apply heading level 1"
        >
          H1
        </button>
        <button
          onClick={() => applyHeading("H2")}
          aria-label="apply heading level 2"
        >
          H2
        </button>
        <button
          onClick={() => applyHeading("H3")}
          aria-label="apply heading level 3"
        >
          H3
        </button>
        <button
          onClick={() => applyHeading("H4")}
          aria-label="apply heading level 4"
        >
          H4
        </button>
        <button
          onClick={() => applyHeading("H5")}
          aria-label="apply heading level 5"
        >
          H5
        </button>
        <button
          onClick={() => applyHeading("H6")}
          aria-label="apply heading level 6"
        >
          H6
        </button>
        <button
          onClick={() => applyFormat("bold")}
          style={{ fontWeight: "bold" }}
          aria-label="apply bold style"
        >
          B
        </button>
        <button
          onClick={() => applyFormat("italic")}
          style={{ fontStyle: "italic" }}
          aria-label="apply italic style"
        >
          I
        </button>
        <button
          onClick={() => applyFormat("underline")}
          style={{ textDecoration: "underline" }}
          aria-label="apply underline style"
        >
          U
        </button>
        <button
          onClick={() => applyFormat("insertUnorderedList")}
          aria-label="create unordered list"
        >
          UL
        </button>
        <button
          onClick={() => applyFormat("insertOrderedList")}
          aria-label="create ordered list"
        >
          OL
        </button>
        <button onClick={handleImageUpload}>Insert Image</button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={insertImageFromFile}
          accept="image/*"
          style={{ display: "none" }}
        />
        <button onClick={insertLink}>Insert Link</button>
        <button onClick={toggleView}>
          {isVisual ? "View HTML" : "View Visual"}
        </button>
      </div>
      {/* <div
        ref={ref as ForwardedRef<HTMLDivElement>}
        contentEditable
        suppressContentEditableWarning
        // onKeyDown={handleKeyDown}
        className="editPart"
      ></div> */}

      {isVisual ? (
        <div
          // ref={ref as ForwardedRef<HTMLDivElement>}
          contentEditable
          suppressContentEditableWarning
          className="editPart"
          ref={editorRef}
        ></div>
      ) : (
        <textarea
          className="editPart"
          value={htmlContent}
          onChange={handleTextareaChange}
        />
      )}

      <button
        onClick={() => {
          const content = isVisual
            ? editorRef.current?.innerHTML ?? ""
            : htmlContent;
          setHtmlContent(content); // Keep it in state too
          onClick?.(content); // Send to parent
        }}
      >
        Save Content
      </button>
    </div>
  );
});

export default TextEditor;
